/**
 * Guests (ROADMAP Phase 8 groundwork, migration 0060): the inbox mirror. Every conversation with
 * whether the guest is waiting on us and for how long (the view's word), the Guest Experience
 * agent's reading of the latest message beside it, and, opened, the messages themselves with
 * any reply the agent has drafted and nobody has decided on yet. Drafts are decided in the
 * Approval Center; NOTHING SENDS from here or anywhere else in this phase — an approved reply
 * is recorded, unsent, until a later phase's connector carries it (AGENTS.md §7: AI drafts,
 * human approves).
 */

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type GuestThreadJson, type GuestThreadDetailJson } from '../api.js';

const URGENCY_LABEL: Record<string, string> = {
  now: 'NOW',
  today: 'today',
  this_week: 'this week',
  none: '—',
};

export function GuestsPage(): ReactNode {
  const [awaitingOnly, setAwaitingOnly] = useState(true);
  const [open, setOpen] = useState<string | null>(null);

  // Every entity at once: an inbox is read whole, and a guest waiting at one LLC's house is not
  // less waiting for the picker being on another. The API still takes ?entity= for a screen
  // that wants one.
  const threads = useQuery({
    queryKey: ['guests', 'threads', awaitingOnly],
    queryFn: () =>
      api.get<{ threads: GuestThreadJson[]; calc_method: string | null }>(
        `/api/guests/threads?awaiting=${awaitingOnly}`,
      ),
  });
  const detail = useQuery({
    queryKey: ['guests', 'thread', open],
    enabled: open !== null,
    queryFn: () =>
      api.get<GuestThreadDetailJson>(`/api/guests/threads/${encodeURIComponent(open!)}`),
  });

  const waiting = (t: GuestThreadJson): string => {
    if (!t.awaiting_reply || t.hours_waiting === null) return '—';
    return t.hours_waiting < 48 ? `${t.hours_waiting} h` : `${Math.floor(t.hours_waiting / 24)} d`;
  };

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Guests</h1>
          <p className="subtitle">
            Every conversation, mirrored from Guesty, with who is waiting on us. The agent reads
            each new message and drafts a reply; a person decides on the draft in{' '}
            <Link to="/approvals">Approvals</Link>. Nothing is sent from Sixgen in this phase.
          </p>
        </div>
      </header>

      <section className="card">
        <h2>
          Conversations{' '}
          <label className="small muted" style={{ fontWeight: 400 }}>
            <input
              type="checkbox"
              checked={awaitingOnly}
              onChange={(e) => setAwaitingOnly(e.target.checked)}
            />{' '}
            waiting on us only
          </label>
        </h2>
        {threads.data && threads.data.threads.length === 0 && (
          <p className="muted">
            {awaitingOnly ? 'Nobody is waiting on a reply.' : 'No conversations mirrored yet.'}
          </p>
        )}
        {threads.data && threads.data.threads.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Guest</th>
                  <th>Stay</th>
                  <th>Waiting</th>
                  <th>Agent says</th>
                  <th className="num">Messages</th>
                </tr>
              </thead>
              <tbody>
                {threads.data.threads.map((t) => (
                  <tr key={t.external_thread_id}>
                    <td>{t.property_name ?? '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="link"
                        onClick={() => setOpen(t.external_thread_id)}
                      >
                        {t.guest_name ?? 'Guest'}
                      </button>
                    </td>
                    <td className="small">
                      {t.check_in ?? '—'} → {t.check_out ?? '—'}
                      {t.reservation_status ? ` · ${t.reservation_status}` : ''}
                    </td>
                    <td>
                      <span className={`chip ${t.awaiting_reply ? 'chip-attention' : 'chip-ok'}`}>
                        {t.awaiting_reply ? waiting(t) : 'replied'}
                      </span>
                    </td>
                    <td className="small">
                      {t.latest_triage ? (
                        <span title={t.latest_triage.why.join(' ')}>
                          {t.latest_triage.sentiment} · urgency{' '}
                          {URGENCY_LABEL[t.latest_triage.urgency] ?? t.latest_triage.urgency}
                          {t.latest_triage.is_complaint ? ' · complaint' : ''}
                          {t.latest_triage.refund_risk !== 'none'
                            ? ` · refund risk ${t.latest_triage.refund_risk}`
                            : ''}
                          {t.latest_triage.review_risk !== 'none'
                            ? ` · review risk ${t.latest_triage.review_risk}`
                            : ''}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num">{t.message_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {threads.data?.calc_method && <p className="muted small">{threads.data.calc_method}</p>}
      </section>

      {open !== null && (
        <section className="card">
          <h2>
            Conversation{' '}
            <button type="button" className="link small" onClick={() => setOpen(null)}>
              close
            </button>
          </h2>
          {detail.isError && <p className="error">{detail.error.message}</p>}
          {detail.data && (
            <>
              <ol className="thread">
                {detail.data.messages.map((m) => (
                  <li key={m.id} className={m.direction === 'inbound' ? 'inbound' : 'outbound'}>
                    <div className="small muted">
                      {m.sender === 'guest'
                        ? 'Guest'
                        : m.sender === 'agent'
                          ? 'Agent draft'
                          : 'Host'}{' '}
                      · {m.sent_at ? new Date(m.sent_at).toLocaleString() : 'not sent'}
                      {m.status !== 'received' && m.status !== 'sent'
                        ? ` · ${m.status}, unsent`
                        : ''}
                    </div>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{m.body}</p>
                    {m.triage && (
                      <p className="small muted">
                        Agent: {m.triage.sentiment}, urgency{' '}
                        {URGENCY_LABEL[m.triage.urgency] ?? m.triage.urgency}
                        {m.triage.is_complaint ? ', complaint' : ''} · {m.triage.issue_category} ·
                        refund risk {m.triage.refund_risk} · review risk {m.triage.review_risk} ·{' '}
                        {m.triage.summary} ({m.triage.confidence})
                      </p>
                    )}
                  </li>
                ))}
              </ol>
              {detail.data.pending_drafts.length > 0 && (
                <div className="card">
                  <h3>Drafted, waiting on you</h3>
                  {detail.data.pending_drafts.map((d) => (
                    <div key={d.approval_id}>
                      <p style={{ whiteSpace: 'pre-wrap' }}>{d.body}</p>
                      <p className="small muted">
                        Decide in <Link to="/approvals">Approvals</Link> · run{' '}
                        {d.run_id.slice(0, 8)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
